'use client';

import type { ProductCard } from '@repo/shopify';
import { useEffect, useMemo, useState } from 'react';
import { ProductGridClient, ProductGridSkeleton } from '@/components/product/product-grid-client';
import { IntentPrefetchLink } from '@/components/ui/intent-prefetch-link';
import { fetchProductBlockProducts } from './product-block-products';
import { getSectionSpacing } from './section-spacing';

interface FeaturedProductsClientProps {
	productHandles: string[];
	heading?: string;
	subheading?: string;
	columns?: 2 | 3 | 4;
	showQuickAdd?: boolean;
	ctaButton?: { label?: string; url?: string };
	sectionSpacing?: string;
	sectionId?: string;
	initialProducts?: ProductCard[];
	moneyLocale?: string;
	fetchOnMount?: boolean;
	priorityImageCount?: number;
}

export function FeaturedProductsClient({
	productHandles,
	heading,
	subheading,
	columns = 4,
	showQuickAdd = true,
	ctaButton,
	sectionSpacing,
	sectionId,
	initialProducts,
	moneyLocale,
	// Server-rendered `initialProducts` (from the cached RSC fetch) are authoritative;
	// only the live-preview path opts into a client refetch.
	fetchOnMount = false,
	priorityImageCount = 0,
}: FeaturedProductsClientProps) {
	const initialProductState =
		initialProducts && initialProducts.length > 0 ? initialProducts : null;
	const [products, setProducts] = useState<ProductCard[] | null>(initialProductState);
	const hasHeader = Boolean(heading || subheading);
	const request = useMemo(
		() => ({ source: 'manual' as const, maxProducts: productHandles.length, productHandles }),
		[productHandles],
	);

	useEffect(() => {
		if (!fetchOnMount) {
			setProducts(initialProductState);
			return;
		}

		const controller = new AbortController();
		setProducts(initialProductState);

		void fetchProductBlockProducts(request, controller.signal)
			.then((nextProducts) => {
				if (!controller.signal.aborted) {
					if (nextProducts.length === 0 && initialProducts && initialProducts.length > 0) {
						return;
					}

					setProducts(nextProducts);
				}
			})
			.catch(() => {
				if (!controller.signal.aborted) {
					if (initialProducts && initialProducts.length > 0) {
						return;
					}

					setProducts([]);
				}
			});

		return () => controller.abort();
	}, [fetchOnMount, initialProductState, initialProducts, request]);

	if (products?.length === 0) return null;
	// The published path never refetches (`fetchOnMount` is only set by live
	// preview), so an empty server result must render nothing — otherwise the
	// skeleton below would be prerendered into the static HTML and pulse forever.
	if (products === null && !fetchOnMount) return null;

	return (
		<section id={sectionId} className={getSectionSpacing(sectionSpacing, { hasHeader })}>
			<div className="container-page">
				{(heading || subheading) && (
					<div className="mb-10 text-center">
						{heading && <h2 className="text-3xl font-bold tracking-tight">{heading}</h2>}
						{subheading && <p className="mt-2 text-neutral-600">{subheading}</p>}
					</div>
				)}

				{products ? (
					<ProductGridClient
						products={products}
						columns={columns}
						showQuickAdd={showQuickAdd}
						priorityImageCount={priorityImageCount}
						moneyLocale={moneyLocale}
					/>
				) : (
					<ProductGridSkeleton columns={columns} count={Math.max(productHandles.length, columns)} />
				)}

				{ctaButton?.label && ctaButton.url && (
					<div className="mt-10 text-center">
						<IntentPrefetchLink
							href={ctaButton.url}
							className="inline-flex items-center rounded-md bg-neutral-900 px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-neutral-800"
						>
							{ctaButton.label}
						</IntentPrefetchLink>
					</div>
				)}
			</div>
		</section>
	);
}
